import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Chip,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { adminOrderService } from '../../services'
import ShopAutocomplete from '../customer-support/ShopAutocomplete'
import AdminDepositLinkOrderField from './AdminDepositLinkOrderField'
import PaymentProofBankBranchFields from './PaymentProofBankBranchFields'
import {
  PAYMENT_METHODS,
  PAYMENT_PROOF_MAX_FILES,
  buildNewFilesFromPending,
  buildPaymentMethodChangePatch,
  buildProofsPayloadFromPending,
  buildSavedAccountPatch,
  canAddAnotherProof,
  canSubmitPendingProofs,
  createEmptyPendingProof,
  getVisibleProofSlotCount,
  isBkashPaymentMethod,
  isCashDepositPaymentMethod,
  isChequePaymentMethod,
  isPendingProofReady,
  requiresDepositorBankAccount,
  supportsSavedGrocerAccountPicker,
  filterSavedAccountsForPaymentPicker,
  isUsingManualGrocerAccount,
  sanitizeAmountInput,
  validatePendingProof,
  validatePaymentProofFile,
} from '../../utils/paymentProofFormState'

export default function AdminShopDepositUploadDialog({
  open,
  onClose,
  shwapnoAccounts = [],
  onUploaded,
}) {
  const [pendingProofs, setPendingProofs] = useState([createEmptyPendingProof()])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [banks, setBanks] = useState([])
  const [savedAccounts, setSavedAccounts] = useState([])
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false)
  const [shop, setShop] = useState(null)
  const [linkOrderId, setLinkOrderId] = useState('')
  // Stable per-file id so per-file form state remounts when files shift.
  const nextLocalIdRef = useRef(1)

  const requestClose = useCallback(() => {
    if (uploading) return
    setDiscardConfirmOpen(true)
  }, [uploading])

  const confirmDiscard = useCallback(() => {
    setDiscardConfirmOpen(false)
    onClose()
  }, [onClose])

  const maxNewFiles = PAYMENT_PROOF_MAX_FILES
  const slotCount = Math.min(
    getVisibleProofSlotCount(pendingProofs),
    maxNewFiles > 0 ? maxNewFiles : 1,
  )

  const selected = pendingProofs[selectedIndex] || null
  const validShwapnoIds = shwapnoAccounts.map((account) => account.id)
  const proofValidationOptions = {
    validShwapnoPaymentSettingsIds: validShwapnoIds,
  }
  const fieldErrors = selected
    ? validatePendingProof(selected, new Date(), proofValidationOptions)
    : {}
  const canSubmit =
    Boolean(shop?.id) &&
    canSubmitPendingProofs(pendingProofs, new Date(), proofValidationOptions) &&
    maxNewFiles > 0
  // Sequential entry: the next "Add" slot stays locked until every added file
  // has complete payment details.
  const addLocked = !canAddAnotherProof(pendingProofs, maxNewFiles)
  const filesAdded = pendingProofs.filter((p) => p?.file).length

  const reset = useCallback(() => {
    setPendingProofs([createEmptyPendingProof()])
    setSelectedIndex(0)
    setError(null)
    setShop(null)
    setLinkOrderId('')
  }, [])

  useEffect(() => {
    if (!open) {
      reset()
      setDiscardConfirmOpen(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const linkedOrderNum = linkOrderId ? parseInt(linkOrderId, 10) : NaN
        const savedPromise = Number.isFinite(linkedOrderNum)
          ? adminOrderService.getOrderSavedPaymentAccounts(linkedOrderNum)
          : shop?.id
            ? adminOrderService.getShopSavedPaymentAccounts(shop.id)
            : Promise.resolve({ data: [] })
        const [bankRes, savedRes] = await Promise.all([
          adminOrderService.getPaymentProofBanks(),
          savedPromise,
        ])
        if (!cancelled) {
          setBanks(bankRes?.data || [])
          setSavedAccounts(savedRes?.data || [])
        }
      } catch {
        if (!cancelled) {
          setBanks([])
          setSavedAccounts([])
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, shop?.id, linkOrderId, reset])

  const patchProof = (index, patch) => {
    setPendingProofs((prev) => {
      const next = [...prev]
      while (next.length <= index) next.push(createEmptyPendingProof())
      next[index] = { ...next[index], ...patch }
      return next
    })
    setError(null)
  }

  // Each new file starts with a blank set of payment details (nothing is
  // copied from the previous file) and is appended after the last file.
  const onPickFiles = (fileList) => {
    const file = fileList?.[0]
    if (!file) return
    if (addLocked) {
      setError('Complete the payment details for this file before adding another.')
      return
    }
    const err = validatePaymentProofFile(file)
    if (err) {
      setError(err)
      return
    }
    const nextIndex = filesAdded
    setPendingProofs((prev) => {
      const next = prev.filter((p) => p?.file)
      next.push({
        ...createEmptyPendingProof(),
        file,
        localId: nextLocalIdRef.current++,
      })
      return next.slice(0, PAYMENT_PROOF_MAX_FILES)
    })
    setSelectedIndex(nextIndex)
    setError(null)
  }

  // Removing a file removes its payment details too.
  const onRemoveFile = (index) => {
    setPendingProofs((prev) => {
      const next = prev.filter((p, i) => i !== index && p?.file)
      return next.length ? next : [createEmptyPendingProof()]
    })
    setSelectedIndex((current) => {
      if (current > index) return current - 1
      if (current === index) return Math.max(0, index - 1)
      return current
    })
    setError(null)
  }

  const handleSubmit = async () => {
    if (!shop?.id || !canSubmit) return
    const newFiles = buildNewFilesFromPending(pendingProofs)
    const proofs = buildProofsPayloadFromPending(pendingProofs)
    if (newFiles.length !== proofs.length) {
      setError('Complete payment details for each uploaded file.')
      return
    }
    try {
      setUploading(true)
      setError(null)
      const orderIdNum = linkOrderId ? parseInt(linkOrderId, 10) : undefined
      const result = await adminOrderService.submitAdminShopDeposit(shop.id, newFiles, {
        proofs,
        orderId: Number.isFinite(orderIdNum) ? orderIdNum : undefined,
      })
      if (typeof onUploaded === 'function') onUploaded(result)
      onClose()
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || 'Failed to upload deposit'
      setError(Array.isArray(msg) ? msg.join(', ') : msg)
    } finally {
      setUploading(false)
    }
  }

  const method = String(selected?.paymentMethod || '').toUpperCase()
  const pickerSavedAccounts = useMemo(
    () => filterSavedAccountsForPaymentPicker(savedAccounts, method),
    [savedAccounts, method],
  )
  const showSavedAccountPicker =
    supportsSavedGrocerAccountPicker(method) && pickerSavedAccounts.length > 0
  const showBkash = isBkashPaymentMethod(method)
  const showCheque = isChequePaymentMethod(method)
  const showDepositorBank =
    requiresDepositorBankAccount(method) &&
    (isUsingManualGrocerAccount(selected) || Boolean(selected?.branchReselectRequired))

  return (
    <>
    <Dialog open={open} onClose={uploading ? undefined : requestClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Upload deposit for shop
        <IconButton onClick={requestClose} disabled={uploading} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <>
            <Box sx={{ mb: 2 }}>
              <ShopAutocomplete
                value={shop}
                onChange={(next) => {
                  setShop(next)
                  setLinkOrderId('')
                }}
                required
                disabled={uploading}
              />
            </Box>
            {shop?.id ? (
              <AdminDepositLinkOrderField
                organizationId={shop.id}
                value={linkOrderId}
                onChange={setLinkOrderId}
                disabled={uploading}
              />
            ) : null}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add up to {maxNewFiles} file(s) (2 MB each, image or PDF). Enter the full payment
              details for each file before adding the next one, matching the partner shop app.
            </Typography>
            {error ? (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            ) : null}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Files</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {Array.from({ length: slotCount }).map((_, index) => {
                const proof = pendingProofs[index]
                const file = proof?.file
                const hasFile = Boolean(file)
                const ready = proof && isPendingProofReady(proof)
                const slotLocked = !hasFile && addLocked
                return (
                  <Box
                    key={`slot-${index}`}
                    component={hasFile || slotLocked ? 'div' : 'label'}
                    aria-disabled={slotLocked || undefined}
                    sx={{
                      width: 108,
                      height: 108,
                      border: '2px dashed',
                      borderColor: selectedIndex === index ? '#11809A' : '#ddd',
                      borderRadius: 2,
                      p: 1,
                      position: 'relative',
                      cursor: slotLocked
                        ? 'not-allowed'
                        : uploading && !hasFile
                          ? 'default'
                          : 'pointer',
                      opacity: slotLocked ? 0.55 : 1,
                      bgcolor: hasFile ? '#f8fafc' : '#fff',
                      boxSizing: 'border-box',
                      ...(hasFile
                        ? {}
                        : {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }),
                    }}
                    onClick={() => {
                      if (hasFile) setSelectedIndex(index)
                    }}
                  >
                    {hasFile && (
                      <Chip
                        size="small"
                        label={ready ? 'Done' : 'Details needed'}
                        color={ready ? 'success' : 'warning'}
                        sx={{ position: 'absolute', top: 4, left: 4, height: 20, fontSize: 10 }}
                      />
                    )}
                    {hasFile && (
                      <IconButton
                        size="small"
                        aria-label="Remove file"
                        disabled={uploading}
                        onClick={(e) => {
                          e.stopPropagation()
                          onRemoveFile(index)
                        }}
                        sx={{ position: 'absolute', top: 2, right: 2, p: 0.25 }}
                      >
                        <DeleteOutlineIcon fontSize="small" color="error" />
                      </IconButton>
                    )}
                    {hasFile ? (
                      <Typography variant="caption" sx={{ mt: 3, display: 'block' }} noWrap>
                        {file.name}
                      </Typography>
                    ) : slotLocked ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textAlign: 'center', lineHeight: 1.3 }}
                      >
                        Complete details first
                      </Typography>
                    ) : (
                      <>
                        {/* The whole slot is a <label>, so clicking anywhere opens the file picker. */}
                        <Button
                          component="span"
                          size="small"
                          startIcon={<CloudUploadIcon />}
                          disabled={uploading}
                          sx={{ pointerEvents: 'none' }}
                        >
                          Add
                        </Button>
                        <input
                          type="file"
                          hidden
                          disabled={uploading}
                          accept="image/*,.pdf,application/pdf"
                          onChange={(e) => {
                            onPickFiles(e.target.files)
                            e.target.value = ''
                          }}
                        />
                      </>
                    )}
                  </Box>
                )
              })}
            </Box>
            {addLocked && filesAdded > 0 && filesAdded < maxNewFiles ? (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: -1, mb: 2 }}>
                Complete the payment details for this file before adding another.
              </Typography>
            ) : null}
            {selected?.file ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle2">{selected.file.name}</Typography>
                <FormControl size="small" fullWidth error={Boolean(fieldErrors.paymentMethod)}>
                  <InputLabel>Payment method</InputLabel>
                  <Select
                    label="Payment method"
                    value={selected.paymentMethod || ''}
                    onChange={(e) =>
                      patchProof(
                        selectedIndex,
                        buildPaymentMethodChangePatch(
                          selected.paymentMethod,
                          e.target.value,
                        ),
                      )
                    }
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {showSavedAccountPicker ? (
                  <FormControl size="small" fullWidth>
                    <InputLabel>Saved shop account</InputLabel>
                    <Select
                      label="Saved shop account"
                      value={
                        selected.accountEntryMode === 'saved' && selected.savedAccountId
                          ? String(selected.savedAccountId)
                          : 'manual'
                      }
                      onChange={(e) => {
                        const value = e.target.value
                        if (value === 'manual') {
                          patchProof(selectedIndex, {
                            savedAccountId: null,
                            accountEntryMode: 'manual',
                            saveAccount: true,
                          })
                          return
                        }
                        const account = pickerSavedAccounts.find(
                          (row) => String(row.id) === value,
                        )
                        patchProof(
                          selectedIndex,
                          buildSavedAccountPatch(account, { banks }),
                        )
                      }}
                    >
                      <MenuItem value="manual">Enter account manually</MenuItem>
                      {pickerSavedAccounts.map((account) => (
                        <MenuItem key={account.id} value={String(account.id)}>
                          {account.label ||
                            `${account.bank_name || account.mfs_name || account.payment_method || 'Account'} · ${account.account_number || account.contact_number || ''}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}
                {showBkash ? (
                  <>
                    <TextField
                      size="small"
                      label="Phone No"
                      placeholder="01XXXXXXXXX"
                      value={selected.bkashPhone || ''}
                      onChange={(e) =>
                        patchProof(selectedIndex, {
                          bkashPhone: e.target.value.replace(/\D/g, '').slice(0, 11),
                        })
                      }
                      error={Boolean(fieldErrors.bkashPhone)}
                      helperText={
                        fieldErrors.bkashPhone || '11 digits, starts with 0'
                      }
                    />
                    <TextField
                      size="small"
                      label="Transaction ID"
                      value={selected.bkashTransactionId || ''}
                      onChange={(e) =>
                        patchProof(selectedIndex, {
                          bkashTransactionId: e.target.value.replace(/[^A-Za-z0-9]/g, ''),
                        })
                      }
                      error={Boolean(fieldErrors.bkashTransactionId)}
                      helperText={
                        fieldErrors.bkashTransactionId || 'Alphanumeric, required'
                      }
                    />
                  </>
                ) : null}
                {showCheque ? (
                  <>
                    <TextField
                      size="small"
                      label="Cheque No"
                      value={selected.chequeNumber || ''}
                      onChange={(e) =>
                        patchProof(selectedIndex, {
                          chequeNumber: e.target.value.slice(0, 10),
                        })
                      }
                      error={Boolean(fieldErrors.chequeNumber)}
                      helperText={fieldErrors.chequeNumber || '3–10 characters, required'}
                    />
                    <TextField
                      size="small"
                      label="Cheque date"
                      type="date"
                      value={selected.chequeDate || ''}
                      onChange={(e) =>
                        patchProof(selectedIndex, { chequeDate: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(fieldErrors.chequeDate)}
                      helperText={fieldErrors.chequeDate || 'Required'}
                    />
                  </>
                ) : null}
                {showDepositorBank ? (
                  <>
                    <PaymentProofBankBranchFields
                        key={selected.localId ?? selectedIndex}
                        banks={banks}
                        proof={selected}
                        errors={fieldErrors}
                        disabled={uploading}
                        onPatch={(patch) => {
                          const nextBankId = Object.prototype.hasOwnProperty.call(patch, 'bankId')
                            ? patch.bankId
                            : selected.bankId
                          const bankChanged = String(nextBankId ?? '') !== String(selected.bankId ?? '')
                          patchProof(selectedIndex, {
                            ...patch,
                            ...(bankChanged
                              ? { savedAccountId: null, accountEntryMode: 'manual' }
                              : {}),
                          })
                        }}
                      />
                    {selected.branchReselectRequired ? (
                      <Typography variant="caption" color="warning.main">
                        This saved branch is no longer listed. Bank and account number are filled — select the branch again.
                      </Typography>
                    ) : null}
                    <TextField
                      size="small"
                      label="Account number"
                      value={selected.accountNumber || ''}
                      onChange={(e) =>
                        patchProof(selectedIndex, {
                          accountNumber: e.target.value.replace(/\D/g, '').slice(0, 17),
                          savedAccountId: null,
                          accountEntryMode: 'manual',
                        })
                      }
                      error={Boolean(fieldErrors.accountNumber)}
                      helperText={fieldErrors.accountNumber}
                    />
                    {selected.accountEntryMode === 'manual' &&
                    !selected.savedAccountId &&
                    String(selected.accountNumber || '').replace(/\D/g, '').length >= 11 ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={Boolean(selected.saveAccount)}
                            disabled={uploading}
                            onChange={(e) =>
                              patchProof(selectedIndex, { saveAccount: e.target.checked })
                            }
                          />
                        }
                        label="Save this account for future payments"
                      />
                    ) : null}
                  </>
                ) : null}
                <TextField
                  size="small"
                  label="Amount paid (৳)"
                  inputProps={{ inputMode: 'decimal' }}
                  value={selected.amount || ''}
                  onChange={(e) =>
                    patchProof(selectedIndex, { amount: sanitizeAmountInput(e.target.value) })
                  }
                  error={Boolean(fieldErrors.amount)}
                  helperText={fieldErrors.amount || undefined}
                />
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    label="Payment date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={selected.paymentDate || ''}
                    onChange={(e) => patchProof(selectedIndex, { paymentDate: e.target.value })}
                    error={Boolean(fieldErrors.paymentDate)}
                    helperText={fieldErrors.paymentDate}
                    sx={{ flex: 1, minWidth: 160 }}
                  />
                  <TextField
                    size="small"
                    label="Payment time"
                    type="time"
                    InputLabelProps={{ shrink: true }}
                    value={selected.paymentTime || ''}
                    onChange={(e) => patchProof(selectedIndex, { paymentTime: e.target.value })}
                    error={Boolean(fieldErrors.paymentTime)}
                    helperText={fieldErrors.paymentTime}
                    sx={{ flex: 1, minWidth: 140 }}
                  />
                </Box>
                <Typography variant="subtitle2">Shwapno receiving account</Typography>
                {shwapnoAccounts.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No Shwapno receiving accounts are configured (same list as the mobile
                    app).
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {shwapnoAccounts.map((account) => {
                      const selectedId =
                        String(selected.shwapnoPaymentSettingsId) ===
                        String(account.id)
                      return (
                        <Chip
                          key={account.id}
                          label={
                            account.label ||
                            `${account.bank_name || account.mfs_name || 'Account'} · ${account.account_number || account.contact_number || ''}`
                          }
                          onClick={() =>
                            patchProof(selectedIndex, {
                              shwapnoPaymentSettingsId: account.id,
                            })
                          }
                          color={selectedId ? 'primary' : 'default'}
                          variant={selectedId ? 'filled' : 'outlined'}
                        />
                      )
                    })}
                  </Box>
                )}
                {fieldErrors.shwapnoPaymentSettingsId ? (
                  <Typography variant="caption" color="error">
                    {fieldErrors.shwapnoPaymentSettingsId}
                  </Typography>
                ) : null}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Select a file slot and add a proof image or PDF.
              </Typography>
            )}
          </>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={uploading}>Cancel</Button>
        <Button
          variant="contained"
          disabled={uploading || !canSubmit}
          onClick={handleSubmit}
          sx={{ bgcolor: '#11809A', '&:hover': { bgcolor: '#0f6d82' } }}
        >
          {uploading ? 'Uploading…' : 'Submit deposit'}
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog
      open={discardConfirmOpen}
      onClose={() => setDiscardConfirmOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Discard payment proof?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          Entered details and any uploaded files will be lost.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDiscardConfirmOpen(false)} color="inherit">
          Cancel
        </Button>
        <Button onClick={confirmDiscard} color="error" variant="contained">
          Discard
        </Button>
      </DialogActions>
    </Dialog>
    </>
  )
}
